import EditOutlined from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import {
  BillingLineKind,
  InvoiceCategoryFilter,
  SettlementState,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { setSettlementSchemaDefaultValues } from '~formsShared/components/SettlementFormFields';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useSettlementLines from '~hooks/useSettlementLines/useSettlementLines';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements, {
  SettlementStateTransition,
} from '~hooks/useSettlements/useSettlements';
import { ExpandedBlockTabs } from '~pages/Approvals/Files/components/styled';
import useSettlementLinesQuery from '~queries/useSettlementLinesQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { canSendEmail } from '../helper';
import SettlementDueDate from '../SettlementDetails/SettlementDueDate';
import SettlementLoadsTab from '../SettlementDetails/SettlementLoadsTab/SettlementLoadsTab';
import SettlementOutboundEmails from '../SettlementDetails/SettlementOutboundEmails';
import SettlementVoidReason from '../SettlementDetails/SettlementVoidReason';
import RemoveAddOnChargeDialog from './RemoveAddOnChargeDialog';
import RemoveChargeDialog from './RemoveChargeDialog';
import {
  areSettlementSheetDraftsValid,
  getSettlementLinesUpdate,
  getSettlementSheetFormValues,
  hasSettlementLinesUpdate,
  SettlementSheetFormValues,
} from './settlementSheetForm';
import {
  groupSettlementSheetLines,
  withoutInvoiceAddOnChargeCopies,
} from './settlementSheetGrouping';
import SettlementSheetHeader from './SettlementSheetHeader';
import SettlementSheetHistory from './SettlementSheetHistory';
import SettlementSheetLineItems from './SettlementSheetLineItems';
import {
  countInvoiceLines,
  getChargeSettlementLines,
  SettlementSheetCharge,
} from './settlementSheetLines';
import SettlementSheetNotes from './SettlementSheetNotes';
import { getSettlementSheetTotals } from './settlementSheetTotalAmounts';
import SettlementSheetTotals from './SettlementSheetTotals';

enum SettlementSheetTab {
  LINE_ITEMS = 'line_items',
  LOADS = 'loads',
  HISTORY = 'history',
}

enum SettlementSheetMutation {
  REMOVE = 'remove',
  SAVE = 'save',
  TRANSITION = 'transition',
}

const EMPTY_FORM_VALUES: SettlementSheetFormValues = {
  addOnCharges: {},
  lineItems: {},
  newAddOnCharges: [],
  notes: '',
};

interface SettlementSheetContentProps {
  category?: InvoiceCategoryFilter;
  onBusyChange: (isBusy: boolean) => void;
  onClose: () => void;
  onSettlementChange: (settlement: Settlement) => void;
  onVoidClick: (settlement: Settlement) => void;
  settlement: Settlement;
}

export default function SettlementSheetContent({
  category,
  onBusyChange,
  onClose,
  onSettlementChange,
  onVoidClick,
  settlement,
}: SettlementSheetContentProps) {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const settlementId = settlement.id ?? '';
  const settlementName = getFriendlySettlementId(settlement, category);
  const { currencyFormatter } = useCompanyCurrency();
  const { removeSettlementAddOnCharge, updateSettlementLines } = useSettlementLines();
  const {
    getSettlementById,
    removeLinesFromSettlement,
    transitionSettlementState,
    updateSettlement,
  } = useSettlements();
  const {
    data: loadedLines,
    isError: hasLinesError,
    isFetching: isLoadingLines,
    refetch: reloadLines,
  } = useSettlementLinesQuery({
    companyId,
    reconcileState: settlement.reconcileState,
    settlementId,
  });
  const [selectedTab, setSelectedTab] = useState(SettlementSheetTab.LINE_ITEMS);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingMutation, setPendingMutation] = useState<SettlementSheetMutation | null>(
    null,
  );
  const [chargeToRemove, setChargeToRemove] = useState<SettlementSheetCharge | null>(
    null,
  );
  const [addOnChargeToRemove, setAddOnChargeToRemove] = useState<AddOnCharge | null>(
    null,
  );
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<SettlementSheetFormValues>({
      defaultValues: EMPTY_FORM_VALUES,
      mode: 'onChange',
    });
  const formValues = watch();

  const isSaving = pendingMutation === SettlementSheetMutation.SAVE;
  const isRemoving = pendingMutation === SettlementSheetMutation.REMOVE;
  const isTransitioning = pendingMutation === SettlementSheetMutation.TRANSITION;
  const isMutating = pendingMutation !== null;
  const hasLoadedLines = loadedLines !== undefined;
  const hasLoadFailed = hasLinesError && !isLoadingLines;
  const isVoided = settlement.state === SettlementState.VOIDED;
  const isRebill = Boolean(settlement.originalSettlement);
  const canEditLines = settlement.editable && !isVoided && !isRebill;
  const isEditingLines = isEditing && canEditLines;
  const drafts = isEditingLines ? formValues : null;
  const lines = useMemo(
    () => groupSettlementSheetLines(loadedLines?.invoiceLines ?? []),
    [loadedLines],
  );
  const settlementAddOnCharges = useMemo(
    () =>
      loadedLines
        ? withoutInvoiceAddOnChargeCopies(
            loadedLines.settlementAddOnCharges,
            loadedLines.invoiceLines,
          )
        : [],
    [loadedLines],
  );
  const invoiceLineCount = countInvoiceLines(loadedLines?.invoiceLines ?? []);
  const totals = useMemo(
    () =>
      getSettlementSheetTotals(
        settlement,
        lines,
        settlementAddOnCharges,
        drafts ?? EMPTY_FORM_VALUES,
      ),
    [drafts, lines, settlement, settlementAddOnCharges],
  );
  const canSaveEdits =
    isEditingLines &&
    !isLoadingLines &&
    !isRemoving &&
    !isTransitioning &&
    areSettlementSheetDraftsValid(lines, settlementAddOnCharges, formValues);
  const chargeToRemoveVoidsSettlement =
    chargeToRemove !== null &&
    getChargeSettlementLines(chargeToRemove).length === invoiceLineCount;
  const addOnChargeToRemoveVoidsSettlement =
    addOnChargeToRemove !== null &&
    lines.invoiceByLineId.has(addOnChargeToRemove.id ?? '') &&
    invoiceLineCount === 1;

  useEffect(() => {
    onBusyChange(isMutating);

    return () => onBusyChange(false);
  }, [isMutating, onBusyChange]);

  const pushSuccess = (messageKey: string) =>
    toasterStore.push(
      alert(t(messageKey, { settlementId: settlementName }), AlertTypes.success),
    );

  const runMutation = async (
    mutation: SettlementSheetMutation,
    perform: () => Promise<void>,
  ) => {
    setPendingMutation(mutation);

    try {
      await perform();
    } catch {
      // reported by the hooks
    } finally {
      setPendingMutation(null);
    }
  };

  const startEditing = () => {
    reset(getSettlementSheetFormValues(settlement, lines, settlementAddOnCharges));
    setIsEditing(true);
  };

  const save = handleSubmit(async (values) => {
    const linesUpdate = getSettlementLinesUpdate(lines, settlementAddOnCharges, values);
    const hasLinesUpdate = hasSettlementLinesUpdate(linesUpdate);
    const notes = values.notes.trim() || null;
    const hasNotesUpdate = notes !== (settlement.notes || null);

    if (!hasLinesUpdate && !hasNotesUpdate) {
      setIsEditing(false);

      return;
    }

    let mayHaveSavedAnything = false;

    await runMutation(SettlementSheetMutation.SAVE, async () => {
      if (hasLinesUpdate) {
        mayHaveSavedAnything = true;
        await updateSettlementLines(companyId, settlementId, linesUpdate);
        setValue('newAddOnCharges', []);
      }

      if (hasNotesUpdate) {
        await updateSettlement(companyId, settlementId, {
          ...omit(setSettlementSchemaDefaultValues(undefined, settlement), 'dueDate'),
          notes,
        });
        mayHaveSavedAnything = true;
      }

      setIsEditing(false);
      pushSuccess('approvals.driver_pay.settlements.sheet.saved');
    });
    void reloadLines();

    if (mayHaveSavedAnything) {
      const refreshedSettlement = await getSettlementById(companyId, settlementId).catch(
        () => null,
      );

      if (refreshedSettlement) {
        onSettlementChange(refreshedSettlement);
      }
    }
  });

  const removeCharge = async (charge: SettlementSheetCharge) => {
    await runMutation(SettlementSheetMutation.REMOVE, async () => {
      onSettlementChange(
        await removeLinesFromSettlement(
          companyId,
          settlementId,
          getChargeSettlementLines(charge),
          chargeToRemoveVoidsSettlement,
        ),
      );
      pushSuccess(
        chargeToRemoveVoidsSettlement
          ? 'approvals.driver_pay.settlements.lines.settlement_voided'
          : 'approvals.driver_pay.settlements.sheet.charge_removed',
      );
    });
    setChargeToRemove(null);
    void reloadLines();
  };

  const removeAddOnCharge = async (addOnCharge: AddOnCharge) => {
    const addOnChargeId = addOnCharge.id ?? '';

    await runMutation(SettlementSheetMutation.REMOVE, async () => {
      if (lines.invoiceByLineId.has(addOnChargeId)) {
        onSettlementChange(
          await removeLinesFromSettlement(
            companyId,
            settlementId,
            [{ id: addOnChargeId, kind: BillingLineKind.ADD_ON_CHARGE }],
            addOnChargeToRemoveVoidsSettlement,
          ),
        );
      } else {
        await removeSettlementAddOnCharge(companyId, settlementId, addOnChargeId);
        onSettlementChange(await getSettlementById(companyId, settlementId));
      }

      pushSuccess(
        addOnChargeToRemoveVoidsSettlement
          ? 'approvals.driver_pay.settlements.lines.settlement_voided'
          : 'approvals.driver_pay.settlements.sheet.add_on_removed',
      );
    });
    setAddOnChargeToRemove(null);
    void reloadLines();
  };

  const transitionState = (transition: SettlementStateTransition) =>
    runMutation(SettlementSheetMutation.TRANSITION, async () => {
      onSettlementChange(
        await transitionSettlementState(companyId, settlementId, transition),
      );
    });

  return (
    <>
      <SettlementSheetHeader
        areActionsDisabled={isEditingLines || isMutating}
        category={category}
        currencyFormatter={currencyFormatter}
        isCloseDisabled={isMutating}
        isTransitioning={isTransitioning}
        onClose={onClose}
        onTransition={transitionState}
        onVoidClick={() => onVoidClick(settlement)}
        settlement={settlement}
        settlementName={settlementName}
        total={totals.total}
      />
      <Box
        sx={(theme) => ({
          bgcolor: theme.semantic.surfaceSunken,
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          p: 2,
        })}
      >
        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <ExpandedBlockTabs
            onChange={(_event, tab: SettlementSheetTab) => setSelectedTab(tab)}
            value={selectedTab}
          >
            <Tab
              label={t('approvals.driver_pay.settlements.line_items')}
              value={SettlementSheetTab.LINE_ITEMS}
            />
            <Tab label={t('common.loads')} value={SettlementSheetTab.LOADS} />
            <Tab
              label={t('approvals.driver_pay.settlements.sheet.history.title')}
              value={SettlementSheetTab.HISTORY}
            />
          </ExpandedBlockTabs>
          {selectedTab === SettlementSheetTab.LINE_ITEMS &&
            canEditLines &&
            (isEditingLines ? (
              <SmallLoadingButton
                color="brandV2Black"
                disabled={!canSaveEdits}
                loading={isSaving}
                onClick={save}
              >
                {t('actions.done')}
              </SmallLoadingButton>
            ) : (
              <SmallButton
                color="secondary"
                disabled={
                  !hasLoadedLines || hasLoadFailed || isLoadingLines || isMutating
                }
                onClick={startEditing}
                startIcon={<EditOutlined />}
                variant="outlined"
              >
                {t('actions.edit')}
              </SmallButton>
            ))}
        </Box>

        {selectedTab === SettlementSheetTab.LINE_ITEMS && (
          <>
            {isLoadingLines && !hasLoadedLines && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}
            {hasLoadFailed && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 3,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('error_messages.settlements.failed_to_fetch_lines')}
                </Typography>
                <SmallButton
                  color="secondary"
                  onClick={() => void reloadLines()}
                  variant="outlined"
                >
                  {t('actions.retry')}
                </SmallButton>
              </Box>
            )}
            {hasLoadedLines && (
              <Box
                sx={{
                  opacity: isLoadingLines || isMutating ? 0.6 : 1,
                  pointerEvents: isLoadingLines || isMutating ? 'none' : 'auto',
                }}
              >
                <SettlementSheetLineItems
                  companyId={companyId}
                  control={control}
                  isAddingAddOnDisabled={isMutating}
                  isEditing={isEditingLines}
                  lines={lines}
                  onRemoveAddOnCharge={setAddOnChargeToRemove}
                  onRemoveCharge={setChargeToRemove}
                  setValue={setValue}
                  settlement={settlement}
                  settlementAddOnCharges={settlementAddOnCharges}
                  totals={totals}
                />
              </Box>
            )}
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <SettlementSheetNotes
                  control={control}
                  isEditing={isEditingLines}
                  notes={settlement.notes ?? null}
                />
                <SettlementDueDate
                  isReadOnly={isVoided || isRebill}
                  onSettlementChange={onSettlementChange}
                  settlement={settlement}
                />
                <SettlementVoidReason voidReason={settlement.voidReason ?? null} />
                {canSendEmail(settlement) && (
                  <SettlementOutboundEmails
                    lastOutboundEmail={settlement.lastOutboundEmail ?? null}
                    settlementId={settlementId}
                  />
                )}
              </Box>
              <SettlementSheetTotals
                currencyFormatter={currencyFormatter}
                totals={totals}
              />
            </Box>
          </>
        )}

        {selectedTab === SettlementSheetTab.LOADS && (
          <SettlementLoadsTab settlement={settlement} />
        )}

        {selectedTab === SettlementSheetTab.HISTORY && (
          <SettlementSheetHistory companyId={companyId} settlement={settlement} />
        )}
      </Box>

      <RemoveChargeDialog
        charge={chargeToRemove}
        chargeTotal={totals.chargeTotals.get(chargeToRemove?.key ?? '') ?? 0}
        currencyFormatter={currencyFormatter}
        isBusy={isRemoving}
        onClose={() => {
          if (!isRemoving) {
            setChargeToRemove(null);
          }
        }}
        onConfirm={() => {
          if (chargeToRemove) {
            void removeCharge(chargeToRemove);
          }
        }}
        settlementName={settlementName}
        voidsSettlement={chargeToRemoveVoidsSettlement}
      />

      <RemoveAddOnChargeDialog
        addOnCharge={addOnChargeToRemove}
        isBusy={isRemoving}
        onClose={() => {
          if (!isRemoving) {
            setAddOnChargeToRemove(null);
          }
        }}
        onConfirm={() => {
          if (addOnChargeToRemove) {
            void removeAddOnCharge(addOnChargeToRemove);
          }
        }}
        settlementName={settlementName}
        voidsSettlement={addOnChargeToRemoveVoidsSettlement}
      />
    </>
  );
}
